import NextRouter from 'next/router'
import { clientSideOnly } from 'shared/client-side-only'
import { domain } from 'shared/domain'

interface NextTransitionOptions {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
}

interface NextUrlObject {
  auth?: string | null | undefined
  hash?: string | null | undefined
  host?: string | null | undefined
  hostname?: string | null | undefined
  href?: string | null | undefined
  protocol?: string | null | undefined
  search?: string | null | undefined
  slashes?: boolean | null | undefined
  port?: string | number | null | undefined
  query?: string | null | undefined | Record<string, any>
}

// eslint-disable-next-line
type Pathname<R> = R | (string & {})

export type BlackBirdOptions = NextTransitionOptions

export interface BlackBirdUrl<R = null> extends NextUrlObject {
  pathname: Pathname<R>
  urlParams?: Record<string, any>
  scroll?: boolean
}

interface TransitionOptions {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
  unstable_skipClientCache?: boolean
}

interface LastNavigation<R> {
  type: 'push' | 'replace' | 'back'
  url: Pathname<R> | BlackBirdUrl<R>
  as?: Pathname<R> | BlackBirdUrl<R>
  options?: TransitionOptions
}

export default class BlackBird<R extends string> {
  private readonly $routerEngine
  private routes
  private lastNavigation: LastNavigation<R> | null = null

  constructor(router: any, routes: readonly string[]) {
    this.$routerEngine = router
    this.routes = routes
  }

  private parseUrlParams(url: BlackBirdUrl<R>) {
    if (url.urlParams && url.pathname) {
      const { urlParams, pathname, ...restUrl } = url
      let result = pathname
      for (const [key, value] of Object.entries(urlParams)) {
        const placeholder = `\${${key}}`
        result = result.split(placeholder).join(value)
      }

      return {
        ...restUrl,
        pathname: result
      }
    }

    return url
  }

  private push(url: Pathname<R> | BlackBirdUrl<R>, options?: TransitionOptions) {
    this.lastNavigation = { type: 'push', url, options }
    return this.$routerEngine.push(url, undefined, options)
  }

  public travelTo(url: Pathname<R> | BlackBirdUrl<R>, options?: TransitionOptions) {
    if (typeof url === 'string') {
      return this.push(url)
    }

    const parsedUrl = this.parseUrlParams(url)
    return this.push(parsedUrl, { ...options })
  }

  public replace(url: Pathname<R> | BlackBirdUrl<R>, as?: Pathname<R> | BlackBirdUrl<R>, options?: BlackBirdOptions) {
    this.lastNavigation = { type: 'replace', url, as, options }
    if (typeof url === 'string') {
      return this.$routerEngine.replace(url, as, options)
    }

    const parsedUrl = this.parseUrlParams(url)
    return this.$routerEngine.replace(parsedUrl, as, options)
  }

  public leave({ shouldRedirect }: { shouldRedirect?: boolean } = {}) {
    if (shouldRedirect) return this.push('/sair?shouldRedirect')
    return this.push('/sair')
  }

  public select({ redirectUrl = '/home' }: { redirectUrl?: string } = {}) {
    this.push({
      pathname: '/trocar-de-conta',
      query: {
        redirectUrl: encodeURIComponent(String(redirectUrl))
      }
    })
  }

  public redirect(url: string) {
    const parsedUrl = new URL(url, domain())

    if (this.routes.includes(parsedUrl.pathname))
      this.push({
        pathname: parsedUrl.pathname,
        query: Object.fromEntries(parsedUrl.searchParams.entries())
      })
    else this.push('/sair')
  }

  public getQuery<T = any>(): Partial<T> {
    return clientSideOnly(() => this.$routerEngine.query, {})
  }

  public goHome() {
    return this.push('/home')
  }

  public getRoutes() {
    return this.routes
  }

  public getPathname() {
    return window.location.pathname
  }

  public reload() {
    window.location.reload()
  }

  public back() {
    this.lastNavigation = { type: 'back', url: '' }
    window.history.back()
  }

  public retry() {
    if (!this.lastNavigation) return

    switch (this.lastNavigation.type) {
      case 'push':
        return this.push(this.lastNavigation.url, this.lastNavigation.options)
      case 'replace':
        return this.replace(this.lastNavigation.url, this.lastNavigation.as, this.lastNavigation.options)
      case 'back':
        return this.back()
    }
  }
}

export function blackBirdFactory<R extends string>(routes: readonly string[]) {
  if (Array.isArray(routes) && routes.length === 0) {
    throw new Error(`Provides a filled array of routes such as: ['/login', '/update-password'] as const`)
  }

  return new BlackBird<R>(NextRouter, routes)
}

export type BlackbirdType = InstanceType<typeof BlackBird>
